import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Edit2, 
  Save, 
  X, 
  Search,
  Building2,
  User,
  Phone,
  Mail,
  Smartphone,
  AlertCircle,
  Plus
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';

// Create Address Modal Component
const CreateAddressModal = ({ onClose, onSubmit, loading, isCompanyAdmin = false, companyDebId = null, companyName = null }) => {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [newAddress, setNewAddress] = useState({
    account_id: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Australia',
    site_code: '',
    contact_name: '',
    contact_phone: '',
    contact_mobile: '',
    contact_email: '',
    is_primary: false
  });

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    if (!isCompanyAdmin) {
      fetchAccounts();
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCompanyAdmin]);

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      // Fetch unique companies by deb_id (not individual users)
      // Since delivery addresses are company-wide, we only need one representative per deb_id
      const { data, error } = await supabase
        .from('accounts')
        .select('id, company_name, full_name, deb_id, email')
        .not('deb_id', 'is', null)
        .order('deb_id', { ascending: true });

      if (error) throw error;
      
      // Group by deb_id to get unique companies
      const uniqueCompanies = [];
      const seenDebIds = new Set();
      
      (data || []).forEach(account => {
        if (!seenDebIds.has(account.deb_id)) {
          seenDebIds.add(account.deb_id);
          uniqueCompanies.push(account);
        }
      });
      
      // Sort by company name
      uniqueCompanies.sort((a, b) => {
        const nameA = (a.company_name || a.full_name || '').toLowerCase();
        const nameB = (b.company_name || b.full_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setAccounts(uniqueCompanies);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      showError(error, 'Failed to fetch customer accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(newAddress);
  };

  const getAccountDisplayName = (account) => {
    const name = account.company_name || account.full_name || account.email;
    return `${name} (Deb ID: ${account.deb_id})`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Create New Delivery Address
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Selection - Only for backend admins */}
                {!isCompanyAdmin && (
                  <div className="md:col-span-2">
                    <h4 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4">Customer Selection</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer / Company <span className="text-red-500">*</span>
                      </label>
                      {loadingAccounts ? (
                        <div className="text-sm text-gray-500">Loading customers...</div>
                      ) : (
                        <select
                          required
                          value={newAddress.account_id}
                          onChange={(e) => setNewAddress({...newAddress, account_id: e.target.value})}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a customer...</option>
                          {accounts.map(account => (
                            <option key={account.id} value={account.id}>
                              {getAccountDisplayName(account)}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                )}
  
                {/* Company Admin Info Banner */}
                {isCompanyAdmin && (
                  <div className="md:col-span-2">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-800">Company Admin Mode</h4>
                          <p className="text-sm text-blue-700 mt-1">
                            Creating delivery address for: <strong>{companyName}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Address Information */}
                <div className="md:col-span-2">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4">Address Information</h4>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={newAddress.address_line1}
                    onChange={(e) => setNewAddress({...newAddress, address_line1: e.target.value})}
                    placeholder="Street address"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    value={newAddress.address_line2}
                    onChange={(e) => setNewAddress({...newAddress, address_line2: e.target.value})}
                    placeholder="Apartment, suite, unit, etc. (optional)"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                    placeholder="City"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={newAddress.state}
                    onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select state...</option>
                    <option value="WA">WA</option>
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="SA">SA</option>
                    <option value="TAS">TAS</option>
                    <option value="ACT">ACT</option>
                    <option value="NT">NT</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={newAddress.postal_code}
                    onChange={(e) => setNewAddress({...newAddress, postal_code: e.target.value})}
                    placeholder="Postal code"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={newAddress.country}
                    onChange={(e) => setNewAddress({...newAddress, country: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* ERP Site Code & Contact Info */}
                <div className="md:col-span-2">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4">
                    {isCompanyAdmin ? 'Contact Information' : 'ERP Site Code & Contact Information'}
                  </h4>
                </div>
  
                {/* Site Code - Only for backend admins */}
                {!isCompanyAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site Code
                    </label>
                    <input
                      type="text"
                      value={newAddress.site_code}
                      onChange={(e) => setNewAddress({...newAddress, site_code: e.target.value})}
                      placeholder="e.g., SITE-MAIN, KALG-01 (optional)"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Leave blank to auto-generate (Site01, Site02, etc.)</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={newAddress.contact_name}
                    onChange={(e) => setNewAddress({...newAddress, contact_name: e.target.value})}
                    placeholder="Contact person name"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={newAddress.contact_phone}
                    onChange={(e) => setNewAddress({...newAddress, contact_phone: e.target.value})}
                    placeholder="Contact phone"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Mobile
                  </label>
                  <input
                    type="tel"
                    value={newAddress.contact_mobile}
                    onChange={(e) => setNewAddress({...newAddress, contact_mobile: e.target.value})}
                    placeholder="Contact mobile"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={newAddress.contact_email}
                    onChange={(e) => setNewAddress({...newAddress, contact_email: e.target.value})}
                    placeholder="Contact email"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newAddress.is_primary}
                      onChange={(e) => setNewAddress({...newAddress, is_primary: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Set as primary delivery address</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Address'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const DeliveryAddressManagement = () => {
  const [addresses, setAddresses] = useState([]);
  const [filteredAddresses, setFilteredAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Company admin state
  const [currentUser, setCurrentUser] = useState(null);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchAddresses();
    }
  }, [currentUser]);

  useEffect(() => {
    filterAddresses();
  }, [searchTerm, addresses]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        
        setCurrentUser(data);
        setIsCompanyAdmin(data.is_company_admin && !data.is_admin);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      showError(error, 'Failed to fetch user details');
    }
  };

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('delivery_addresses')
        .select(`
          id,
          address_line1,
          address_line2,
          city,
          state,
          postal_code,
          country,
          site_code,
          contact_name,
          contact_phone,
          contact_mobile,
          contact_email,
          is_primary,
          account_id,
          company_deb_id,
          accounts!delivery_addresses_account_id_fkey (
            id,
            company_name,
            full_name,
            deb_id
          )
        `)
        .order('company_deb_id', { ascending: true });

      // Filter by company for company admins
      if (isCompanyAdmin && currentUser) {
        query = query.eq('company_deb_id', currentUser.deb_id.toString());
      }

      const { data, error } = await query;

      if (error) throw error;
      setAddresses(data || []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      showError(error, 'Failed to fetch delivery addresses');
    } finally {
      setLoading(false);
    }
  };

  const filterAddresses = () => {
    if (!searchTerm.trim()) {
      setFilteredAddresses(addresses);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = addresses.filter(addr => 
      (addr.accounts?.company_name?.toLowerCase().includes(term)) ||
      (addr.accounts?.full_name?.toLowerCase().includes(term)) ||
      (addr.accounts?.deb_id?.toLowerCase().includes(term)) ||
      (addr.site_code?.toLowerCase().includes(term)) ||
      (addr.address_line1?.toLowerCase().includes(term)) ||
      (addr.city?.toLowerCase().includes(term)) ||
      (addr.contact_name?.toLowerCase().includes(term))
    );

    setFilteredAddresses(filtered);
  };

  const handleEdit = (address) => {
    setEditingId(address.id);
    setEditForm({
      site_code: address.site_code || '',
      contact_name: address.contact_name || '',
      contact_phone: address.contact_phone || '',
      contact_mobile: address.contact_mobile || '',
      contact_email: address.contact_email || '',
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || 'Australia'
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const validateSiteCode = async (siteCode, companyDebId, currentAddressId) => {
    if (!siteCode.trim()) {
      return true; // Empty site code is allowed
    }

    // Check if site code is unique for this company
    const { data, error } = await supabase
      .from('delivery_addresses')
      .select('id, site_code')
      .eq('company_deb_id', companyDebId)
      .eq('site_code', siteCode)
      .neq('id', currentAddressId);

    if (error) {
      console.error('Error validating site code:', error);
      return false;
    }

    return data.length === 0;
  };

  const handleSave = async (address) => {
    // Validate site code uniqueness per company
    if (editForm.site_code && editForm.site_code.trim()) {
      const isUnique = await validateSiteCode(
        editForm.site_code,
        address.company_deb_id,
        address.id
      );

      if (!isUnique) {
        showError(
          new Error('Duplicate site code'),
          `Site code "${editForm.site_code}" is already used for this company. Please use a unique code.`
        );
        return;
      }
    }

    setSaving(true);
    try {
      const updateData = {
        contact_name: editForm.contact_name.trim() || null,
        contact_phone: editForm.contact_phone.trim() || null,
        contact_mobile: editForm.contact_mobile.trim() || null,
        contact_email: editForm.contact_email.trim() || null,
        address_line1: editForm.address_line1.trim(),
        address_line2: editForm.address_line2.trim() || null,
        city: editForm.city.trim(),
        state: editForm.state,
        postal_code: editForm.postal_code.trim(),
        country: editForm.country.trim() || 'Australia'
      };

      // Only backend admins can update site_code
      if (!isCompanyAdmin) {
        updateData.site_code = editForm.site_code.trim() || null;
      }

      const { error } = await supabase
        .from('delivery_addresses')
        .update(updateData)
        .eq('id', address.id);

      if (error) throw error;

      showNotification('Delivery address updated successfully', 'success');
      setEditingId(null);
      setEditForm({});
      fetchAddresses();
    } catch (error) {
      console.error('Error updating address:', error);
      showError(error, 'Failed to update delivery address');
    } finally {
      setSaving(false);
    }
  };

  const getCompanyName = (address) => {
    return address.accounts?.company_name || address.accounts?.full_name || 'Unknown';
  };

  const handleCreateAddress = async (newAddress) => {
    setCreating(true);
    try {
      let accountData;
      
      // For company admins, use their own account data
      if (isCompanyAdmin) {
        accountData = {
          deb_id: currentUser.deb_id,
          company_name: currentUser.company_name,
          full_name: currentUser.full_name
        };
        // Set account_id to current user's id for company admins
        newAddress.account_id = currentUser.id;
      } else {
        // For backend admins, get the account details from the selected account
        const { data, error: accountError } = await supabase
          .from('accounts')
          .select('deb_id, company_name, full_name')
          .eq('id', newAddress.account_id)
          .single();

        if (accountError) throw accountError;
        if (!data.deb_id) {
          throw new Error('Selected customer does not have a deb_id');
        }
        accountData = data;
      }

      // Validate site code uniqueness if provided (only for backend admins)
      if (!isCompanyAdmin && newAddress.site_code && newAddress.site_code.trim()) {
        const isUnique = await validateSiteCode(
          newAddress.site_code,
          accountData.deb_id.toString(),
          null // No current address ID for new addresses
        );

        if (!isUnique) {
          showError(
            new Error('Duplicate site code'),
            `Site code "${newAddress.site_code}" is already used for this company. Please use a unique code.`
          );
          setCreating(false);
          return;
        }
      }

      // Get the current user ID for created_by field
      const { data: { user } } = await supabase.auth.getUser();

      // Insert the new delivery address
      const { error: insertError } = await supabase
        .from('delivery_addresses')
        .insert([
          {
            account_id: newAddress.account_id,
            company_deb_id: accountData.deb_id.toString(),
            address_line1: newAddress.address_line1.trim(),
            address_line2: newAddress.address_line2.trim() || null,
            city: newAddress.city.trim(),
            state: newAddress.state,
            postal_code: newAddress.postal_code.trim(),
            country: newAddress.country.trim() || 'Australia',
            site_code: isCompanyAdmin ? null : (newAddress.site_code.trim() || null),
            contact_name: newAddress.contact_name.trim() || null,
            contact_phone: newAddress.contact_phone.trim() || null,
            contact_mobile: newAddress.contact_mobile.trim() || null,
            contact_email: newAddress.contact_email.trim() || null,
            is_primary: newAddress.is_primary,
            created_by: user?.id || null
          }
        ]);

      if (insertError) throw insertError;

      showNotification('Delivery address created successfully', 'success');
      setShowCreateModal(false);
      fetchAddresses();
    } catch (error) {
      console.error('Error creating address:', error);
      showError(error, 'Failed to create delivery address');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Delivery Addresses"
        description={isCompanyAdmin
          ? `Manage delivery addresses for ${currentUser?.company_name || 'your company'}`
          : 'Manage ERP site codes and contact information for customer delivery addresses.'}
        meta={
          <span className="flex items-center space-x-2">
            <MapPin className="w-4 h-4" />
            <span>{addresses.length} address{addresses.length !== 1 ? 'es' : ''}</span>
          </span>
        }
        actions={
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            icon={<Plus className="w-4 h-4" />}
          >
            Create New Address
          </Button>
        }
      />

      {/* Info Banner */}
      {!isCompanyAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">About Site Codes</p>
              <p>Site codes are used by the ERP system to identify specific delivery locations. Each site code must be unique per customer. Leave blank for one-time deliveries (ERP will auto-generate "Site01", "Site02", etc.).</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search by company, site code, address, or contact..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>

      {/* Addresses List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="h-3 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : filteredAddresses.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <MapPin className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No addresses found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria.' : 'No delivery addresses have been created yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAddresses.map((address) => {
            const isEditing = editingId === address.id;
            
            return (
              <motion.div
                key={address.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  {/* Customer Info */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <Building2 className="w-5 h-5 text-gray-400" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {getCompanyName(address)}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Deb ID: {address.accounts?.deb_id || 'Not set'}
                          {address.is_primary && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Primary
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {!isEditing && (
                      <button
                        onClick={() => handleEdit(address)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Address Section */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Delivery Address</h4>
                      {isEditing ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Address Line 1 <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              required
                              value={editForm.address_line1}
                              onChange={(e) => setEditForm({ ...editForm, address_line1: e.target.value })}
                              placeholder="Street address"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Address Line 2
                            </label>
                            <input
                              type="text"
                              value={editForm.address_line2}
                              onChange={(e) => setEditForm({ ...editForm, address_line2: e.target.value })}
                              placeholder="Apartment, suite, etc."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              City <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              required
                              value={editForm.city}
                              onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                              placeholder="City"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              State <span className="text-red-500">*</span>
                            </label>
                            <select
                              required
                              value={editForm.state}
                              onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select state...</option>
                              <option value="WA">WA</option>
                              <option value="NSW">NSW</option>
                              <option value="VIC">VIC</option>
                              <option value="QLD">QLD</option>
                              <option value="SA">SA</option>
                              <option value="TAS">TAS</option>
                              <option value="ACT">ACT</option>
                              <option value="NT">NT</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Postal Code <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              required
                              value={editForm.postal_code}
                              onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
                              placeholder="Postal code"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Country
                            </label>
                            <input
                              type="text"
                              value={editForm.country}
                              onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">
                          {address.address_line1}
                          {address.address_line2 && <>, {address.address_line2}</>}
                          <br />
                          {address.city}, {address.state} {address.postal_code}
                          {address.country && <><br />{address.country}</>}
                        </p>
                      )}
                    </div>

                    {/* ERP & Contact Section */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        {isCompanyAdmin ? 'Contact Info' : 'ERP Site Code & Contact Info'}
                      </h4>
                      
                      {isEditing ? (
                        <div className="space-y-3">
                          {/* Site Code Input - Only for backend admins */}
                          {!isCompanyAdmin && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Site Code
                              </label>
                              <input
                                type="text"
                                value={editForm.site_code}
                                onChange={(e) => setEditForm({ ...editForm, site_code: e.target.value })}
                                placeholder="e.g., SITE-MAIN, KALG-01"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          )}

                          {/* Contact Name Input */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Contact Name
                            </label>
                            <input
                              type="text"
                              value={editForm.contact_name}
                              onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                              placeholder="Contact person name"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Contact Phone Input */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Phone
                            </label>
                            <input
                              type="tel"
                              value={editForm.contact_phone}
                              onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                              placeholder="Contact phone"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Contact Mobile Input */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Mobile
                            </label>
                            <input
                              type="tel"
                              value={editForm.contact_mobile}
                              onChange={(e) => setEditForm({ ...editForm, contact_mobile: e.target.value })}
                              placeholder="Contact mobile"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Contact Email Input */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Email
                            </label>
                            <input
                              type="email"
                              value={editForm.contact_email}
                              onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                              placeholder="Contact email"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex space-x-2 pt-2">
                            <button
                              onClick={() => handleSave(address)}
                              disabled={saving}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                              <Save className="w-4 h-4 mr-1" />
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancel}
                              disabled={saving}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2 text-sm text-gray-600">
                          {!isCompanyAdmin && (
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="font-medium">Site Code:</span>
                              <span className="ml-2">{address.site_code || <span className="text-gray-400 italic">Not set</span>}</span>
                            </div>
                          )}
                          {address.contact_name && (
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-2 text-gray-400" />
                              <span>{address.contact_name}</span>
                            </div>
                          )}
                          {address.contact_phone && (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 mr-2 text-gray-400" />
                              <span>{address.contact_phone}</span>
                            </div>
                          )}
                          {address.contact_mobile && (
                            <div className="flex items-center">
                              <Smartphone className="w-4 h-4 mr-2 text-gray-400" />
                              <span>{address.contact_mobile}</span>
                            </div>
                          )}
                          {address.contact_email && (
                            <div className="flex items-center">
                              <Mail className="w-4 h-4 mr-2 text-gray-400" />
                              <span>{address.contact_email}</span>
                            </div>
                          )}
                          {!address.contact_name && !address.contact_phone && !address.contact_mobile && !address.contact_email && (
                            <p className="text-gray-400 italic">No contact information</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create Address Modal */}
      {showCreateModal && (
        <CreateAddressModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateAddress}
          loading={creating}
          isCompanyAdmin={isCompanyAdmin}
          companyDebId={currentUser?.deb_id}
          companyName={currentUser?.company_name}
        />
      )}
    </div>
  );
};

export default DeliveryAddressManagement;
